import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { cn } from "@/lib/utils";
import Consultant from "../../../models/Consultant";
import { ConsultantTagType } from "../../../models/Organisation";
import { UserLastActivity } from "../../../networking/consultant.hooks";
import { chartColors } from "../../../theme/chartColors";
import Rating from "../../Core/Rating/Rating";
import ProfilePictureReborn from "../../Core/ProfilePicture/ProfilePictureReborn";
import WeeklyAvailability from "../WeeklyAvailability/WeeklyAvailability";
import { DetailHeaderField, DetailHeaderTags, DetailHeaderTextField } from "../DetailHeader";
import { generateAvatarString } from "../../../utils/string.utils";
import { tagHoursLeft } from "../../../utils/credential.utils";

interface ConsultantHeaderProps {
	consultant: Consultant;
	lastActivity?: UserLastActivity;
	/** Org consultant tag taxonomy, used to resolve the consultant's `{ key }` tags to name/color. */
	tagTypes?: ConsultantTagType[];
	/** When provided, an inline "Lägg till tagg" picker is shown after the tags. Toggles the key. */
	onToggleTag?: (key: string) => void;
	onUploadProfilePicture: (pic: any) => void;
	className?: string;
}

/**
 * Profile summary header shown at the top of the consultant detail page — avatar plus a
 * harmonized grid of contact info, status, ratings and app activity.
 *
 * Next-gen (Tailwind) replacement for the inline Chakra/styled-components header that used to
 * live in `ConsultantsDetailsPage`. Reuses the (still shared) `Rating`, `WeeklyAvailability`
 * and `ProfilePictureReborn` primitives.
 */
const ConsultantHeader: React.FC<ConsultantHeaderProps> = ({
	consultant,
	lastActivity,
	tagTypes,
	onToggleTag,
	onUploadProfilePicture,
	className,
}) => {
	const { t } = useTranslation();
	const { profile } = consultant;
	const ratings = profile?.ratings;

	const managerName =
		profile?.manager?.firstName && profile?.manager?.lastName
			? `${profile.manager.firstName} ${profile.manager.lastName}`
			: "-";

	// Resolve the consultant's `{ key }` tags against the org taxonomy (falling back to the raw key
	// for keys deleted from the taxonomy). Expired tags count as unassigned, so they're skipped.
	const tags = (profile?.tags ?? [])
		.filter((held) => !held.expiresAt || new Date(held.expiresAt).getTime() > Date.now())
		.map((held) => {
			const type = tagTypes?.find((tagType) => tagType.key === held.key);
			const name = type?.name.sv ?? held.key;
			// Timed tags show the hours left on hover so a renewal is verifiable.
			const hoursLeft = tagHoursLeft(held.expiresAt);
			const title =
				hoursLeft !== null
					? `${name} · ${t("consultantTag.hoursLeft", { count: hoursLeft })}`
					: name;
			return { key: held.key, name, color: type?.color, title };
		});

	const tagOptions = (tagTypes ?? []).map((tagType) => ({
		key: tagType.key,
		name: tagType.name.sv,
		color: tagType.color,
	}));

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
				<div className="flex w-fit shrink-0 self-center lg:self-start">
					<ProfilePictureReborn
						name={generateAvatarString(`${consultant.firstName} ${consultant.lastName}`)}
						onUploadProfilePicture={onUploadProfilePicture}
						image={profile?.profilePicture}
					/>
				</div>

				<div className="flex flex-1 flex-wrap items-start justify-start gap-x-16 gap-y-8">
					{/* Contact */}
					<div className="flex w-[270px] shrink-0 flex-col gap-3.5">
						<DetailHeaderTextField
							label={t("EMPLOYEE_NO")}
							value={profile?.employeeNumber}
							copyable
						/>
						<DetailHeaderTextField
							label={t("PHONE")}
							value={profile?.phoneNumber}
							href={profile?.phoneNumber ? `tel:${profile.phoneNumber}` : undefined}
							copyable
						/>
						<DetailHeaderTextField
							label={t("EMAIL")}
							value={consultant.email}
							href={consultant.email ? `mailto:${consultant.email}` : undefined}
							copyable
						/>
					</div>

					{/* Status */}
					<div className="flex w-[150px] shrink-0 flex-col gap-3.5">
						<DetailHeaderTextField
							label={t("STATUS")}
							value={consultant.active ? t("ACTIVE") : t("INACTIVE")}
						/>
						<DetailHeaderTextField label={t("JOBS")} value={profile?.noOfJobs || 0} />
						<DetailHeaderTextField
							label={t("ORGANISATION_CONSULTANT_MANAGER")}
							value={managerName}
						/>
					</div>

					{/* Ratings */}
					<div className="flex shrink-0 flex-col gap-3.5">
						<DetailHeaderField label={t("effortRating")}>
							<Rating
								initialValue={ratings?.performanceAverage || 0}
								fillColor={chartColors.brand}
								size={22}
								readonly
								className="-ml-1"
							/>
						</DetailHeaderField>
						<DetailHeaderField label={t("punctualityRating")}>
							<Rating
								initialValue={ratings?.punctualityScoreAverage || 0}
								fillColor={chartColors.brand}
								size={22}
								readonly
								className="-ml-1"
							/>
						</DetailHeaderField>
						{!!ratings?.average && !!ratings?.count && (
							<DetailHeaderField label={t("OPINION")}>
								{`${ratings.average} (${ratings.count} omdömen)`}
							</DetailHeaderField>
						)}
					</div>

					{/* Availability & app activity */}
					<div className="flex shrink-0 flex-col gap-3.5">
						<div className="h-11">
							<WeeklyAvailability availability={profile?.availability} />
						</div>
						{lastActivity?.date && (
							<DetailHeaderField label={t("LAST_ACTIVE_ON_APP")}>
								{`${moment(lastActivity.date).format("YYYY-MM-DD, HH:mm")} (${
									lastActivity.platform
								} ${lastActivity.appVersion || "-"})`}
							</DetailHeaderField>
						)}
					</div>
				</div>
			</div>

			{/* Tags — full-width row under the avatar, no label */}
			<DetailHeaderTags tags={tags} options={tagOptions} onToggleTag={onToggleTag} />
		</div>
	);
};

export default ConsultantHeader;
